import { ErrorConverter } from '../../errorHandling/ErrorConverter';
import { ErrorHandler } from '../../errorHandling/ErrorHandler';
import { CapturedApiError } from '../../errorHandling/errorHandling.types';
import { useTranslation } from '../../hooks/useTranslation.hook';
import { ReactQueryHandlerOptions } from './reactQuery.interfaces';

/**
 * Thin React Query error handler wrapper.
 *
 * Delegates entirely to ErrorHandler — no inline routing, no stray
 * Promise.reject, no unhandled rejection risk. The ErrorHandler owns
 * translation, toast/boundary routing, LogMessage submission, and dedup.
 */
export function useErrorHandler<T>(
  queryHandlerOptions?: ReactQueryHandlerOptions<T>
) {
  const { t } = useTranslation();

  return (capturedError: CapturedApiError | null) => {
    if (capturedError && Object.keys(capturedError).length > 0) {
      const error = { ...capturedError };

      ErrorHandler.getInstance().handleApiError(error, {
        errorHandling: queryHandlerOptions?.errorHandling,
        customHandledFaultCodes: queryHandlerOptions?.customHandledFaultCodes,
        customFaultHandler: queryHandlerOptions?.customFaultHandler,
        scope: queryHandlerOptions?.scope,
        translateMessage: queryHandlerOptions?.errorMessage
          ? (err) => {
              const faultCode = ErrorConverter.getFaultCode(
                err.applicationContext?.apiMessage?.response?.data?.Fault
              );
              const result = queryHandlerOptions.errorMessage!(faultCode, t);
              // @ts-ignore - legacy pattern: errorMessage returns a map keyed by fault code
              return result?.[faultCode] || undefined;
            }
          : undefined
      });
    }
  };
}
